import { ref } from "vue";

type TooltipHandlers = {
  position: () => void;
  close: () => void;
};

const activeTooltipId = ref<string | null>(null);
const handlersById = new Map<string, TooltipHandlers>();

let listenersAttached = false;

function withActiveHandlers(fn: (handlers: TooltipHandlers) => void) {
  const id = activeTooltipId.value;
  if (!id) return;
  const handlers = handlersById.get(id);
  if (!handlers) return;
  fn(handlers);
}

function attachGlobalListeners() {
  if (listenersAttached) return;
  if (typeof window === "undefined") return;

  window.addEventListener(
    "scroll",
    () => {
      withActiveHandlers((h) => h.position());
    },
    true
  );
  window.addEventListener("resize", () => {
    withActiveHandlers((h) => h.position());
  });
  document.addEventListener("click", () => {
    withActiveHandlers((h) => h.close());
  });
  listenersAttached = true;
}

export function useTooltipRegistry() {
  function registerTooltip(id: string, handlers: TooltipHandlers) {
    handlersById.set(id, handlers);
    return () => {
      handlersById.delete(id);
      if (activeTooltipId.value === id) {
        activeTooltipId.value = null;
      }
    };
  }

  function openTooltip(id: string) {
    activeTooltipId.value = id;
    attachGlobalListeners();
  }

  function closeTooltip(id: string) {
    if (activeTooltipId.value === id) {
      activeTooltipId.value = null;
    }
  }

  return { activeTooltipId, registerTooltip, openTooltip, closeTooltip };
}
